import { useState, useEffect } from 'react';
import { QUESTIONS, CONSTRAINT_BUCKETS } from '../data/wiseCheckQuestions';
import WiseCheckResults from './WiseCheckResults';
import './WiseCheck.css';
import { gaTrackEvent } from "../utils/ga";

const WiseCheck = () => {
  const [currentQuestionId, setCurrentQuestionId] = useState('Q1');
  const [answers, setAnswers] = useState({});
  const [scores, setScores] = useState({
    [CONSTRAINT_BUCKETS.DEMAND]: 0,
    [CONSTRAINT_BUCKETS.CONVERSION]: 0,
    [CONSTRAINT_BUCKETS.DELIVERY]: 0,
    [CONSTRAINT_BUCKETS.FINANCIAL]: 0,
    [CONSTRAINT_BUCKETS.PEOPLE]: 0
  });
  useEffect(() => {
  gaTrackEvent("wise_check_started");
}, []);
  const [flags, setFlags] = useState([]);
  const [completedPaths, setCompletedPaths] = useState([]);
  const [history, setHistory] = useState(['Q1']);
  const [showResults, setShowResults] = useState(false);
  const [contactInfo, setContactInfo] = useState({ name: '', email: '', phone: '', business_name: '' });
  const [q2bAnswers, setQ2bAnswers] = useState([]); // Track all Q2B selections for reporting

  const currentQuestion = QUESTIONS[currentQuestionId];

  // Calculate progress (rough estimate)
  const calculateProgress = () => {
    const totalQuestions = Object.keys(answers).length;
    const estimatedTotal = 15; // Approximate based on average path
    return Math.min((totalQuestions / estimatedTotal) * 100, 95);
  };

  const updateScores = (newScores) => {
    setScores(prev => {
      const updated = { ...prev };
      Object.entries(newScores).forEach(([bucket, points]) => {
        updated[bucket] = (updated[bucket] || 0) + points;
      });
      return updated;
    });
  };

  const addFlag = (flag) => {
    if (flag && !flags.includes(flag)) {
      setFlags(prev => [...prev, flag]);
    }
  };

  const handleAnswer = (option) => {
    // Save answer
    setAnswers(prev => ({ ...prev, [currentQuestionId]: option.value }));

    // Update scores if present
    if (option.score) {
      updateScores(option.score);
    }

    // Add flags if present
    if (option.flag) {
      addFlag(option.flag);
    }

    // Handle path tracking for Q2 (primary constraint)
    if (currentQuestionId === 'Q2' && option.path) {
      setCompletedPaths(prev => [...prev, option.path]);
    }

    // Navigate to next question
    if (option.next) {
      setCurrentQuestionId(option.next);
      setHistory(prev => [...prev, option.next]);
    }
  };

  // Handle Q2B radio selection (single choice that loops)
  const handleQ2BAnswer = (option) => {
    // Track this selection for the summary
    if (option.value !== 'none') {
      setQ2bAnswers(prev => [...prev, option.label]);
    }
    
    // Save answer
    setAnswers(prev => ({ 
      ...prev, 
      [`Q2B_${completedPaths.length + 1}`]: option.value // Track each Q2B iteration
    }));

    // Update scores if present
    if (option.score) {
      updateScores(option.score);
    }

    // Map Q2B option values to their path IDs
    const pathMapping = {
      'lead_generation': 'MARKETING',
      'conversion': 'SALES',
      'operational': 'OPERATIONS',
      'financial': 'FINANCIAL',
      'team_gaps': 'PEOPLE'
    };

    if (option.value === 'none') {
      // User selected "No other issues" - save all Q2B selections and move to context
      setAnswers(prev => ({ ...prev, Q2B_all: q2bAnswers }));
      setCurrentQuestionId('INDUSTRY');
      setHistory(prev => [...prev, 'INDUSTRY']);
    } else {
      // Mark this path as completed and go explore it
      const pathId = pathMapping[option.value];
      if (pathId) {
        setCompletedPaths(prev => [...prev, pathId]);
      }
      setCurrentQuestionId(option.next);
      setHistory(prev => [...prev, option.next]);
    }
  };

  const handleTextInput = (value) => {
    setAnswers(prev => ({ ...prev, [currentQuestionId]: value }));
  };

  const handleTextSubmit = () => {
    if (currentQuestion.next) {
      setCurrentQuestionId(currentQuestion.next);
      setHistory(prev => [...prev, currentQuestion.next]);
    }
  };

  const handleContactSubmit = (e) => {
    e.preventDefault();
    
    // Save contact info
    setAnswers(prev => ({ ...prev, CONTACT: contactInfo }));

    // Apply risk amplifiers
    applyRiskAmplifiers();

    // Move to results
    setCurrentQuestionId('RESULTS');
    setShowResults(true);
  };

  const applyRiskAmplifiers = () => {
    const amplifier = 1.25;
    const riskFlags = ['owner_dependent', 'no_documentation', 'no_tracking', 'key_person_risk'];
    
    const hasRiskFlags = flags.some(flag => riskFlags.includes(flag));

    if (hasRiskFlags) {
      setScores(prev => {
        const updated = { ...prev };
        Object.keys(updated).forEach(bucket => {
          if (updated[bucket] > 40) {
            updated[bucket] = Math.round(updated[bucket] * amplifier);
          }
        });
        return updated;
      });
    }
  };

  const handleBack = () => {
    if (history.length > 1) {
      const newHistory = [...history];
      newHistory.pop(); // Remove current
      const previousQuestion = newHistory[newHistory.length - 1];
      setHistory(newHistory);
      setCurrentQuestionId(previousQuestion);
    }
  };

  // Skip directly to results if Q6 answer is "diagnosis"
  useEffect(() => {
    if (currentQuestionId === 'Q6' && answers.Q6 === 'diagnosis') {
      applyRiskAmplifiers();
      setShowResults(true);
    }
  }, [currentQuestionId, answers.Q6]);

  if (showResults) {
    return <WiseCheckResults scores={scores} answers={answers} flags={flags} />;
  }

  if (!currentQuestion) {
    return <div className="wise-check-container">Loading...</div>;
  }

  return (
    <div className="wise-check-container" data-testid="wise-check-container">
      {/* Progress Bar */}
      <div className="progress-bar-container" data-testid="progress-bar">
        <div className="progress-bar" style={{ width: `${calculateProgress()}%` }}></div>
      </div>

      <div className="wise-check-content">
        {/* Back Button */}
        {history.length > 1 && (
          <button 
            onClick={handleBack} 
            className="back-button"
            data-testid="back-button"
          >
            ← Back
          </button>
        )}

        {/* Question */}
        <div className="question-container" data-testid="question-container">
          <h2 className="question-title" data-testid="question-title">{currentQuestion.question}</h2>

          {/* Single Choice */}
          {currentQuestion.type === 'single' && currentQuestionId !== 'Q2B' && (
            <div className="options-container" data-testid="options-container">
              {currentQuestion.options.map((option, index) => (
                <button
                  key={index}
                  onClick={() => handleAnswer(option)}
                  className="option-button"
                  data-testid={`option-${option.value}`}
                >
                  {option.label}
                </button>
              ))}
            </div>
          )}

          {/* Q2B - Radio Button Loop (filters out completed paths) */}
          {currentQuestionId === 'Q2B' && (
            <div className="options-container" data-testid="q2b-container">
              {currentQuestion.options
                .filter(option => {
                  // Filter out already completed paths
                  if (option.value !== 'none') {
                    const pathMapping = {
                      'lead_generation': 'MARKETING',
                      'conversion': 'SALES',
                      'operational': 'OPERATIONS',
                      'financial': 'FINANCIAL',
                      'team_gaps': 'PEOPLE'
                    };
                    const pathId = pathMapping[option.value];
                    return !completedPaths.includes(pathId);
                  }
                  return true; // Always show "No other issues" option
                })
                .map((option, index) => (
                  <button
                    key={index}
                    onClick={() => handleQ2BAnswer(option)}
                    className={`option-button ${option.value === 'none' ? 'option-button-secondary' : ''}`}
                    data-testid={`q2b-option-${option.value}`}
                  >
                    {option.label}
                  </button>
                ))}
            </div>
          )}

          {/* Text Input */}
          {currentQuestion.type === 'text' && (
            <div className="text-input-container" data-testid="text-input-container">
              <input
                type="text"
                className="text-input"
                placeholder="Enter your industry..."
                value={answers[currentQuestionId] || ''}
                onChange={(e) => handleTextInput(e.target.value)}
                data-testid="text-input"
              />
              <button
                onClick={handleTextSubmit}
                className="submit-button"
                disabled={!answers[currentQuestionId]}
                data-testid="text-submit"
              >
                Continue
              </button>
            </div>
          )}

          {/* Contact Form */}
          {currentQuestion.type === 'contact' && (
            <form onSubmit={handleContactSubmit} className="contact-form" data-testid="contact-form">
              <input
                type="text"
                placeholder="Your Name *"
                required
                value={contactInfo.name}
                onChange={(e) => setContactInfo({ ...contactInfo, name: e.target.value })}
                className="contact-input"
                data-testid="contact-name"
              />
              <input
                type="email"
                placeholder="Email Address *"
                required
                value={contactInfo.email}
                onChange={(e) => setContactInfo({ ...contactInfo, email: e.target.value })}
                className="contact-input"
                data-testid="contact-email"
              />
              <input
                type="tel"
                placeholder="Phone Number *"
                required
                value={contactInfo.phone}
                onChange={(e) => setContactInfo({ ...contactInfo, phone: e.target.value })}
                className="contact-input"
                data-testid="contact-phone"
              />
              <input
                type="text"
                placeholder="Business Name"
                value={contactInfo.business_name}
                onChange={(e) => setContactInfo({ ...contactInfo, business_name: e.target.value })}
                className="contact-input"
                data-testid="contact-business"
              />
              <button type="submit" className="submit-button" data-testid="contact-submit">
                Get My Results
              </button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
};

export default WiseCheck;
